import { Link } from 'react-router-dom';
import { CheckCircle, CreditCard, FileText, Award, GraduationCap, Shield, Users, ArrowRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';

export const MembresiaPage = () => {
  const benefits = [
    { icon: Shield, title: 'Validación Documental', description: 'Verificación de tus credenciales, certificaciones y experiencia profesional' },
    { icon: FileText, title: 'Perfil Público Verificado', description: 'Aparece en el directorio oficial del CNI con sello de verificación' },
    { icon: Award, title: 'Certificado Digital', description: 'Certificado PDF con QR verificable que acredita tu membresía' },
    { icon: GraduationCap, title: 'Descuentos Académicos', description: 'Hasta 15% de descuento en toda la oferta académica avalada' },
    { icon: Users, title: 'Red Profesional', description: 'Acceso a la red de profesionales inmobiliarios certificados' },
    { icon: CreditCard, title: 'Datos Fiscales', description: 'Captura de datos fiscales para facturación (próximamente)' }
  ];

  return (
    <div className="min-h-screen bg-[#111111] py-12" data-testid="membresia-page">
      <div className="container-cni">
        {/* Header */}
        <div className="text-center mb-12">
          <Badge className="bg-amber-500/10 text-amber-400 border-amber-500/20 mb-4">
            Membresía Anual
          </Badge>
          <h1 className="font-manrope font-bold text-3xl md:text-4xl text-white mb-4">
            Únete al Colegio Nacional de Inmobiliarios
          </h1>
          <p className="text-gray-400 max-w-2xl mx-auto">
            Obtén tu membresía anual y accede a todos los beneficios de pertenecer al registro 
            público verificable de profesionales inmobiliarios más confiable de México.
          </p>
        </div>

        {/* Pricing Card */}
        <div className="max-w-lg mx-auto mb-16">
          <div className="bg-gradient-to-b from-[#1a1a1a] to-[#151515] border border-amber-500/20 rounded-2xl p-8 text-center relative overflow-hidden">
            <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-amber-500 to-amber-400" />
            
            <h2 className="font-manrope font-semibold text-xl text-white mb-2">Membresía Profesional</h2>
            <p className="text-gray-500 mb-6">Vigencia de 12 meses</p>
            
            <div className="mb-6">
              <span className="text-5xl font-manrope font-bold text-white">$1,500</span>
              <span className="text-xl text-gray-400 ml-2">MXN</span>
              <p className="text-sm text-gray-500 mt-1">+ IVA ($1,740 MXN total)</p>
            </div>

            <ul className="text-left space-y-3 mb-8">
              <li className="flex items-center gap-3 text-gray-300">
                <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0" />
                Validación documental completa
              </li>
              <li className="flex items-center gap-3 text-gray-300">
                <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0" />
                Perfil público con verificación CNI
              </li>
              <li className="flex items-center gap-3 text-gray-300">
                <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0" />
                Certificado digital con QR
              </li>
              <li className="flex items-center gap-3 text-gray-300">
                <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0" />
                Descuentos en oferta académica
              </li>
              <li className="flex items-center gap-3 text-gray-300">
                <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0" />
                Acceso a la red profesional
              </li>
            </ul>

            <Link to="/registro">
              <Button className="w-full h-12 bg-amber-500 hover:bg-amber-400 text-black font-semibold">
                Registrarme Ahora
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
            
            <p className="text-xs text-gray-600 mt-4">
              Pago seguro procesado por Stripe
            </p>
          </div>
        </div>

        {/* Benefits Grid */}
        <div className="mb-16">
          <h2 className="font-manrope font-bold text-2xl text-white text-center mb-8">
            Beneficios de la Membresía
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {benefits.map((benefit, index) => (
              <div 
                key={index}
                className="bg-[#1a1a1a] border border-[#2a2a2a] rounded-xl p-6 hover:border-amber-500/30 transition-colors"
              >
                <div className="w-12 h-12 bg-amber-500/10 rounded-lg flex items-center justify-center mb-4">
                  <benefit.icon className="w-6 h-6 text-amber-400" />
                </div>
                <h3 className="font-semibold text-white mb-2">{benefit.title}</h3>
                <p className="text-gray-500 text-sm">{benefit.description}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Process */}
        <div className="bg-[#1a1a1a] border border-[#2a2a2a] rounded-2xl p-8 md:p-12 mb-16">
          <h2 className="font-manrope font-bold text-2xl text-white text-center mb-8">
            Proceso de Membresía
          </h2>
          <div className="grid md:grid-cols-4 gap-8">
            <div className="text-center">
              <div className="w-12 h-12 bg-amber-500 rounded-full flex items-center justify-center mx-auto mb-4 text-black font-bold">1</div>
              <h3 className="font-semibold text-white mb-2">Registro</h3>
              <p className="text-gray-500 text-sm">Crea tu cuenta con tus datos básicos</p>
            </div>
            <div className="text-center">
              <div className="w-12 h-12 bg-amber-500 rounded-full flex items-center justify-center mx-auto mb-4 text-black font-bold">2</div>
              <h3 className="font-semibold text-white mb-2">Pago</h3>
              <p className="text-gray-500 text-sm">Realiza el pago de tu membresía anual</p>
            </div>
            <div className="text-center">
              <div className="w-12 h-12 bg-amber-500 rounded-full flex items-center justify-center mx-auto mb-4 text-black font-bold">3</div>
              <h3 className="font-semibold text-white mb-2">Documentación</h3>
              <p className="text-gray-500 text-sm">Sube tus documentos para validación</p>
            </div>
            <div className="text-center">
              <div className="w-12 h-12 bg-amber-500 rounded-full flex items-center justify-center mx-auto mb-4 text-black font-bold">4</div>
              <h3 className="font-semibold text-white mb-2">Activación</h3>
              <p className="text-gray-500 text-sm">Recibe tu certificado y aparece en el directorio</p>
            </div>
          </div>
        </div>

        {/* FAQ Preview */}
        <div className="text-center">
          <h2 className="font-manrope font-bold text-2xl text-white mb-4">
            ¿Tienes dudas?
          </h2>
          <p className="text-gray-400 mb-6">
            Consulta cómo funciona el proceso o contáctanos directamente
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/como-funciona">
              <Button variant="outline" className="border-[#2a2a2a] text-gray-300 hover:bg-[#1a1a1a]">
                Cómo Funciona
              </Button>
            </Link>
            <Link to="/contacto">
              <Button variant="outline" className="border-[#2a2a2a] text-gray-300 hover:bg-[#1a1a1a]">
                Contactar
              </Button>
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};
